import React, { useEffect, useState } from 'react'
import { HeadingPages } from '../../../components/HeadingPages'
import { Box, Button, Flex, Input, Select, Spinner, TableContainer, Text } from '@chakra-ui/react'
import { useForm } from 'react-hook-form'
import { useQuery } from 'react-query'
import { api } from '../../../services/api'
import { ValidacaoDeNfLista } from './ValidacaoDeNfLista'
import * as T from '../../../components/Table'
import { GerarExcel } from './GerarExcel'
import { useEnterprise } from '../../../hooks/useEnterprise'
import { format } from 'date-fns'

async function getNf(filters) {
  const response = await api.get('/site/v2/fiscal/nf-validada', {
    params: {
      data_nf_de: filters.data_nf_de,
      data_nf_ate: filters.data_nf_ate,
      data_compra_de: filters.data_compra_de,
      data_compra_ate: filters.data_compra_ate,
      nfe: filters.nfe,
      pedido_compra: filters.pedido_compra,
      fornecedor: filters.fornecedor,
      empresa: filters.empresa,
      ...filters,
    },
  })
  return response.data
}

export const ValidacaoDeNf = () => {
  const { register, handleSubmit, setValue } = useForm()
  const [filters, setFilters] = useState({})
  const { data: empresas } = useEnterprise()

  useEffect(() => {
    // Removendo a inicialização automática das datas
    const defaultFilters = {
      data_nf_de: '',
      data_nf_ate: '',
      data_compra_de: '',
      data_compra_ate: '',
      nfe: '',
      pedido_compra: '',
      fornecedor: '',
      empresa: '',
      pendente: 0, // Mantendo apenas o filtro "Pendente" como padrão
    }

    setFilters(defaultFilters)

    // Removendo o setValue para as datas, mas mantendo para o pendente
    setValue('pendente', 0)
  }, [setValue])

  const {
    data = [],
    isLoading,
    isRefetching,
  } = useQuery(['nfData', filters], () => getNf(filters), {
    refetchOnWindowFocus: false,
    // Modificando a condição para permitir a busca mesmo sem datas,
    // desde que tenha o valor pendente definido
    enabled:
      !!filters.pendente !== undefined || !!(filters.data_nf_de && filters.data_nf_ate) || !!(filters.data_compra_de && filters.data_compra_ate),
  })

  const onSubmit = (formData) => {
    setFilters(formData)
  }

  const options = [
    {
      label: 'Pendente',
      value: 0,
    },
    {
      label: 'Concluídos',
      value: 1,
    },
  ]

  return (
    <>
      <HeadingPages name="Validação de NF" />

      <Flex flexDir="column" experimental_spaceY={4} mt={4}>
        <Flex flexDir="column" as="form" onSubmit={handleSubmit(onSubmit)}>
          {/* Primeira linha do grid */}
          <Flex alignItems="end" justifyContent="center" gap={2} mb={3}>
            <Flex flexDir="column">
              <Text as="label" fontSize="sm" htmlFor="data-nf-de">
                NFE De
              </Text>
              <Input {...register('data_nf_de')} id="data-nf-de" size="sm" type="date" />
            </Flex>

            <Flex flexDir="column">
              <Text as="label" fontSize="sm" htmlFor="data-nf-ate">
                NFE Até
              </Text>
              <Input {...register('data_nf_ate')} id="data-nf-ate" size="sm" type="date" />
            </Flex>

            <Flex flexDir="column">
              <Text as="label" fontSize="sm" htmlFor="data-compra-de">
                Pedido Compra De
              </Text>
              <Input {...register('data_compra_de')} id="data-compra-de" size="sm" type="date" />
            </Flex>

            <Flex flexDir="column">
              <Text as="label" fontSize="sm" htmlFor="data-compra-ate">
                Pedido Compra Até
              </Text>
              <Input {...register('data_compra_ate')} id="data-compra-ate" size="sm" type="date" />
            </Flex>

            <Flex flexDir="column">
              <Text as="label" fontSize="sm" htmlFor="nfe">
                Nfe
              </Text>
              <Input {...register('nfe')} size="sm" id="nfe" />
            </Flex>
          </Flex>

          {/* Segunda linha do grid */}
          <Flex alignItems="end" justifyContent="center" gap={2}>
            <Flex flexDir="column">
              <Text as="label" fontSize="sm" htmlFor="pedido_compra">
                Pedido Compra
              </Text>
              <Input {...register('pedido_compra')} size="sm" id="pedido_compra" />
            </Flex>

            <Flex flexDir="column">
              <Text as="label" fontSize="sm" htmlFor="numero_pedido">
                Pedido de Venda
              </Text>
              <Input {...register('numero_pedido')} size="sm" id="numero_pedido" />
            </Flex>

            <Flex flexDir="column">
              <Text as="label" fontSize="sm" htmlFor="fornecedor">
                Fornecedor
              </Text>
              <Input {...register('fornecedor')} size="sm" id="fornecedor" />
            </Flex>

            <Flex flexDir="column">
              <Text as="label" fontSize="sm" htmlFor="empresa">
                Empresa
              </Text>
              <Select {...register('empresa')} size="sm" id="empresa">
                <option value="">Todos</option>
                {empresas?.map((item) => (
                  <option key={item.codigo_empresa} value={item.nome_empresa}>
                    {item.nome_empresa}
                  </option>
                ))}
              </Select>
            </Flex>

            <Flex flexDir="column">
              <Text as="label" fontSize="sm" htmlFor="pendente">
                Pendente
              </Text>
              <Select {...register('pendente')} size="sm" id="pendente">
                <option value="">Todos</option>
                {options?.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </Select>
            </Flex>

            <Button
              isLoading={isRefetching || isLoading}
              loadingText="Buscando..."
              size="sm"
              colorScheme="blue"
              ml="4"
              maxW="120px"
              w="full"
              type="submit"
            >
              Buscar
            </Button>
          </Flex>
        </Flex>

        <Flex gap={6} alignItems="center">
          <GerarExcel data={data} />

          <Flex justifyContent="start" alignItems="center" gap={2}>
            <Box p={2} bg="red.50" boxShadow="md">
              <Text fontSize="sm" color="red.500">
                Vermelho: Pendente para avaliação fiscal
              </Text>
            </Box>
            <Box p={2} bg="green.50" boxShadow="md">
              <Text fontSize="sm" color="green.500">
                Verde: Validada pelo fiscal
              </Text>
            </Box>
          </Flex>

          {!isLoading && !isRefetching && data && (
            <Box p={2} bg="blue.50" boxShadow="md">
              <Text fontSize="sm" color="blue.500">
                Total de registros: {data.length}
              </Text>
            </Box>
          )}
        </Flex>

        <TableContainer>
          <T.Table>
            <T.Thead>
              <T.Th>Data Pedido Compra</T.Th>
              <T.Th>Data Emissão NFE</T.Th>
              <T.Th>Data Validação</T.Th>
              <T.Th>Pedido</T.Th>
              <T.Th>Número do pedido</T.Th>
              <T.Th>Fornecedor</T.Th>
              <T.Th>NFe</T.Th>
              <T.Th>Chave Nf</T.Th>
              <T.Th>Nota/Pdf</T.Th>
              <T.Th>Valor NF</T.Th>
              <T.Th>Valor Pedido Compra</T.Th>
              <T.Th>Status</T.Th>
              <T.Th>Erros</T.Th>

              <T.Th>Confirmar</T.Th>
              <T.Th />
              <T.Th>Excluir</T.Th>
            </T.Thead>
            <T.Tbody>
              {!isLoading && !isRefetching && <ValidacaoDeNfLista data={data} />}
              {isLoading && isRefetching && (
                <T.Tr>
                  <T.Td colSpan="14" textAlign="center">
                    Carregando... <Spinner size="sm" />
                  </T.Td>
                </T.Tr>
              )}
            </T.Tbody>
          </T.Table>
        </TableContainer>
      </Flex>
    </>
  )
}
